import React from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeftOutlined,
  MinusCircleOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { Button, Form, Input, InputNumber, Modal, Space, message } from "antd";
import "./AdminTools.css";
import { useAuth } from "../AuthProvider";
import APIService from "../../Service/APIService";
import Table from "../../common/table";

const ShippingSetup = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const apiServiceRef = React.useRef(new APIService());
  const [form] = Form.useForm();

  const [shippingCategories, setShippingCategories] = React.useState([]);
  const [isShippingCategoriesLoading, setIsShippingCategoriesLoading] =
    React.useState(false);
  const [isShippingCategoryModalOpen, setIsShippingCategoryModalOpen] =
    React.useState(false);
  const [isSavingShippingCategory, setIsSavingShippingCategory] =
    React.useState(false);
  const [editingShippingCategory, setEditingShippingCategory] =
    React.useState(null);

  const getErrorMessage = (error, fallback) =>
    error?.response?.data?.message || fallback;

  const getShippingRatesFromCategory = (shippingCategory) => {
    const sourceRates =
      shippingCategory?.ShippingRates ||
      shippingCategory?.shippingRates ||
      shippingCategory?.Rates ||
      [];

    if (!Array.isArray(sourceRates) || sourceRates.length === 0) {
      return [{ ShippingRateName: "", ShippingRate: null }];
    }

    return sourceRates.map((rate) => ({
      ID: rate?.ID,
      ShippingCategoryID:
        rate?.ShippingCategoryID ?? shippingCategory?.ID ?? null,
      ShippingRateName: rate?.ShippingRateName || "",
      ShippingRate: rate?.ShippingRate ?? null,
    }));
  };

  const fetchShippingCategories = React.useCallback(async () => {
    setIsShippingCategoriesLoading(true);
    try {
      const response = await apiServiceRef.current.getDataFromBackend(
        "getshippingcategories"
      );
      setShippingCategories(response?.data?.data || []);
    } catch (error) {
      message.error(getErrorMessage(error, "Failed to load shipping categories"));
    } finally {
      setIsShippingCategoriesLoading(false);
    }
  }, []);

  React.useEffect(() => {
    const userData = user;
    if (!userData) return;

    if (userData.Type !== "Admin" || !userData.IsSuperAdmin) {
      navigate("/");
      return;
    }

    fetchShippingCategories();
  }, [fetchShippingCategories, navigate, user]);

  const closeShippingCategoryModal = () => {
    setIsShippingCategoryModalOpen(false);
    setEditingShippingCategory(null);
    form.resetFields();
  };

  const openCreateShippingCategoryModal = () => {
    setEditingShippingCategory(null);
    form.resetFields();
    form.setFieldsValue({
      ShippingCategoryName: "",
      ShippingRates: [{ ShippingRateName: "", ShippingRate: null }],
    });
    setIsShippingCategoryModalOpen(true);
  };

  const openEditShippingCategoryModal = (shippingCategory) => {
    setEditingShippingCategory(shippingCategory);
    form.setFieldsValue({
      ShippingCategoryName: shippingCategory?.ShippingCategoryName,
      ShippingRates: getShippingRatesFromCategory(shippingCategory),
    });
    setIsShippingCategoryModalOpen(true);
  };

  const handleSaveShippingCategory = async () => {
    try {
      const values = await form.validateFields();
      setIsSavingShippingCategory(true);

      const payload = {
        ShippingCategoryName: values.ShippingCategoryName.trim(),
        ShippingRates: (values.ShippingRates || []).map((rate) => ({
          ...(rate?.ID ? { ID: rate.ID } : {}),
          ShippingCategoryID:
            editingShippingCategory?.ID ?? rate?.ShippingCategoryID ?? null,
          ShippingRateName: rate.ShippingRateName.trim(),
          ShippingRate: Number(rate.ShippingRate),
        })),
      };

      if (editingShippingCategory) {
        await apiServiceRef.current.updateDataToBackend(
          { ...payload, ID: editingShippingCategory.ID },
          "updateshippingcategory"
        );
        message.success("Shipping category updated successfully");
      } else {
        await apiServiceRef.current.addDataToBackend(
          payload,
          "addshippingcategory"
        );
        message.success("Shipping category created successfully");
      }

      closeShippingCategoryModal();
      await fetchShippingCategories();
    } catch (error) {
      if (error?.errorFields) return;
      message.error(getErrorMessage(error, "Unable to save shipping category"));
    } finally {
      setIsSavingShippingCategory(false);
    }
  };

  const shippingcategoryColumns = [
    {
      headerName: "Shipping Category Name",
      field: "ShippingCategoryName",
      filter: true,
      minWidth: 280,
    },
    {
      headerName: "Action",
      field: "",
      sortable: false,
      filter: false,
      minWidth: 140,
      cellRenderer: (params) => (
        <Button
          type="link"
          onClick={() => openEditShippingCategoryModal(params.data)}
          style={{ padding: 0 }}
        >
          Edit
        </Button>
      ),
    },
  ];

  return (
    <div className="admin-tools-wrapper">
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: "16px",
          flexWrap: "wrap",
        }}
      >
        <div>
          <div className="text text-h1">Shipping Setup</div>
          <div className="text text-smallText admin-tools-subtitle">
            Configure shipping categories used during order management.
          </div>
        </div>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate("/AdminTools")}>
          Back to Admin Tools
        </Button>
      </div>

      <div className="admin-tools-row">
        <div className="admin-tools-banner-alerts">
          <div className="admin-tools-banner-alerts-header">
            <div className="text text-h2">Shipping Categories</div>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={openCreateShippingCategoryModal}
            >
              New Shipping Category
            </Button>
          </div>

          <Table
            rowData={shippingCategories}
            columnDefs={shippingcategoryColumns}
            loading={isShippingCategoriesLoading}
          />
        </div>
      </div>

      <Modal
        title={
          editingShippingCategory
            ? "Edit Shipping Category"
            : "New Shipping Category"
        }
        open={isShippingCategoryModalOpen}
        onCancel={closeShippingCategoryModal}
        onOk={handleSaveShippingCategory}
        okText="Confirm"
        cancelText="Cancel"
        confirmLoading={isSavingShippingCategory}
        destroyOnClose
        width={680}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="Shipping Category Name"
            name="ShippingCategoryName"
            rules={[
              {
                required: true,
                message: "Shipping Category Name is required",
              },
              {
                max: 200,
                message: "Shipping Category Name cannot exceed 200 characters",
              },
            ]}
          >
            <Input maxLength={200} />
          </Form.Item>

          <Form.List name="ShippingRates">
            {(fields, { add, remove }) => (
              <>
                <div className="text text-bodyText text-bold">Shipping Rates</div>
                <div className="spacer-8" />

                {fields.map((field) => (
                  <Space
                    key={field.key}
                    align="baseline"
                    style={{
                      display: "flex",
                      marginBottom: 8,
                      width: "100%",
                    }}
                  >
                    <Form.Item
                      {...field}
                      label="Shipping Rate Name"
                      name={[field.name, "ShippingRateName"]}
                      style={{ minWidth: 250, marginBottom: 0 }}
                      rules={[
                        {
                          required: true,
                          message: "Shipping Rate Name is required",
                        },
                        {
                          max: 200,
                          message:
                            "Shipping Rate Name cannot exceed 200 characters",
                        },
                      ]}
                    >
                      <Input maxLength={200} />
                    </Form.Item>

                    <Form.Item
                      {...field}
                      label="Shipping Rate"
                      name={[field.name, "ShippingRate"]}
                      style={{ minWidth: 180, marginBottom: 0 }}
                      rules={[
                        { required: true, message: "Shipping Rate is required" },
                      ]}
                    >
                      <InputNumber
                        min={0}
                        precision={2}
                        style={{ width: "100%" }}
                      />
                    </Form.Item>

                    {fields.length > 1 && (
                      <MinusCircleOutlined
                        onClick={() => remove(field.name)}
                        style={{ color: "#ff4d4f", marginTop: 6 }}
                      />
                    )}
                  </Space>
                ))}

                <Form.Item style={{ marginTop: 12, marginBottom: 0 }}>
                  <Button
                    type="dashed"
                    onClick={() =>
                      add({
                        ShippingRateName: "",
                        ShippingRate: null,
                        ShippingCategoryID: editingShippingCategory?.ID ?? null,
                      })
                    }
                    icon={<PlusOutlined />}
                    block
                  >
                    Add Shipping Rate
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>
        </Form>
      </Modal>
    </div>
  );
};

export default ShippingSetup;
